import { Model } from '@vuex-orm/core'
import _get from 'lodash/get'
import _sumBy from 'lodash/sumBy'
import roundToFixed from '@/utils/roundToFixed'
import CartItemModel from '@/models/CartItemModel'
import LineItemModel from '@/models/LineItemModel'
import CustomerModel from '@/models/CustomerModel'
import AvailabilityModel from '@/models/AvailabilityModel'

const SERVICE_FEE_PERCENTAGE = 0.05

export default class CartModel extends Model {
  static entity = 'cart'

  static nameSingular = 'Cart'
  static namePlural = 'Carts'

  static fields() {
    return {
      id: this.uid(),
      cartItems: this.hasMany(CartItemModel, 'cart_id'),
      shop_id: this.attr(null),
      sales_tax: this.number(0),
      delivery_fee: this.number(0),
      custom_delivery_fee: this.number().nullable(),
      tip: this.number().nullable(),
      special_instructions: this.string(''),
      fulfillment_slot: this.string('').nullable(),
      fulfillment_type: this.string('').nullable(),
      availability_id: this.attr(null),
      availability: this.belongsTo(AvailabilityModel, 'availability_id'),
      delivery_address: this.string('').nullable(),
      is_custom: this.boolean(0),
      is_gift: this.boolean(),
      giftee_details: this.attr(),
      user_id: this.attr(null),
      customer: this.belongsTo(CustomerModel, 'user_id'),
      completed: this.boolean(),
      ready_for_guest: this.boolean(),
      paid: this.boolean(),
    }
  }

  get family_discount() {
    if (!this.user_id) return 0
    let customer = CustomerModel.find(this.user_id)
    return _get(customer, 'family_discount', 0)
  }

  /**
   * Get the price of all items
   */
  get subtotal() {
    return _sumBy(this.products, 'subtotal')
  }

  /**
   * Get the price of all items in USD
   */
  get subtotal_usd() {
    return this.subtotal / 100
  }

  get discounts_total() {
    return _sumBy(this.discounts, (i) => i.price * i.quantity)
  }

  /**
   * Get the price of all items in USD
   * minus the applied discounts
   */
  get subtotal_incl_discounts() {
    return this.subtotal - this.discounts_total - this.family_discount_amount
  }

  /**
   * Get the sales tax amount as fraction of 100
   */
  get sales_tax_pcnt() {
    return this.sales_tax / 100
  }

  /**
   * Get the sales tax amount in USD.
   */
  get sales_tax_amount() {
    let base = this.subtotal_incl_discounts * this.sales_tax_pcnt
    return base < 0 ? 0 : base
  }

  /**
   * Get the sales tax amount in USD.
   */
  get sales_tax_usd() {
    return this.sales_tax_amount / 100
  }

  /**
   * Get the sales tax amount in USD.
   */
  get sales_tax_formatted() {
    return `$${roundToFixed(this.sales_tax_usd)}`
  }

  /**
   * Get the family discount as a percantage
   * (fraction of 100)
   */
  get family_discount_pcnt() {
    return Math.abs(this.family_discount / 100)
  }

  /**
   * Get the family discount as a string with %
   */
  get family_discount_pcnt_formatted() {
    return `${this.family_discount}%`
  }

  /**
   * Get the family discount amount in cents
   * Subtotal times Family Discount %
   */
  get family_discount_amount() {
    return this.subtotal * this.family_discount_pcnt
  }

  /**
   * Get the family discount amount as USD
   */
  get family_discount_amount_usd() {
    return this.family_discount_amount / 100
  }

  /**
   * Get delivery fee based on the order_type
   */
  get delivery_fee_applied() {
    if (this.is_custom && this.custom_delivery_fee) {
      return this.custom_delivery_fee
    }
    return this.fulfillment_type === 'Delivery' ? this.delivery_fee : null
  }

  /**
   * Get tip in cents
   */
  get tip_in_cents() {
    return this.tip ? this.tip : 0
  }
  /**
   * Get tip in USD
   */
  get tip_in_usd() {
    return this.tip / 100
  }
  /**
   * Get service fee in readable percentage like "5" for 0.05
   */
  get service_fee_percentage() {
    return SERVICE_FEE_PERCENTAGE * 100
  }

  get service_fee_in_cents() {
    if (!this.$store().getters['shop/getFeatureFlagServiceFee']) {
      return 0
    }
    return this.subtotal * SERVICE_FEE_PERCENTAGE
  }

  get service_fee() {
    return this.service_fee_in_cents / 100
  }

  get service_fee_applied() {
    if (this.$store().getters['shop/getShopHasActiveTrial']) {
      return 0
    }
    return this.service_fee_in_cents
  }

  get service_fee_formatted() {
    return `$${roundToFixed(this.service_fee)}`
  }

  /**
   * Get the order total.
   */
  get total() {
    let total = this.subtotal_incl_discounts
    total += this.delivery_fee_applied
    total += this.tip_in_cents
    total += this.sales_tax_amount
    total += this.service_fee_applied

    return total
  }

  /**
   * Get the order total in USD.
   */
  get total_usd() {
    return this.total / 100
  }

  get products() {
    return this.cartItems.filter(
      (li) => li.type === LineItemModel.lineItemTypes.PRODUCT
    )
  }
  get fees() {
    return this.cartItems.filter(
      (li) => li.type === LineItemModel.lineItemTypes.FEE
    )
  }
  get discounts() {
    return this.cartItems.filter(
      (li) => li.type === LineItemModel.lineItemTypes.DISCOUNT
    )
  }

  get isValid() {
    let hasFulfillmentInfo =
      this.fulfillment_slot.length && this.fulfillment_type.length
    if (!hasFulfillmentInfo) return false

    let isCustomerSelected = this.user_id !== null
    let subtotalGtZero = this.subtotal > 0
    let discountsNotZero = this.discounts_total !== 0
    let totalNotZero = this.total !== 0
    let hasItems = this.cartItems.length > 0
    return (
      (isCustomerSelected && subtotalGtZero) ||
      (!subtotalGtZero && (discountsNotZero || totalNotZero || hasItems))
    )
  }
}
